import * as React from "react";
import moment from 'moment'
import { useLocalize } from "@/modules/shared/context/localize/LocalizeContext";
import { RepeatableSection } from "../components/RepeatableSection";
import { CurrencyModel } from "@/app/models/newDelegation/currencyModel";
import { DriverQualificationsType } from "@/app/constants/driverQualificationsType";
import { getDriverQualificationTypeName } from "@/app/common/converters/driverQualificationToTextConverter";
import { GenericMinimumWageExpandableItemModel } from "@/app/models/configuration/minimumWage/genericMinimumWageConfigurationModel";
import { DriverWorkYearsPeriods } from "@/app/constants/configuration/driverWorkYearsPeriods";
import { getDriverWorkYearsPeriodsWorkName } from "@/app/common/converters/configuration/driverWorkYearsPeriodsToTextConverter";
import { getDriverQualificationType } from "@/app/services/contract/driverQualificationTypeResolver";
import { SERVICE_DATE_FORMAT } from "@/app/constants/date";

type Props = {
    driverWorkYears?: DriverWorkYearsPeriods[]
    allowedCurrencies?: string[]
    qualificationTypes?: DriverQualificationsType[]
    state: {
        configuration: { currencies: CurrencyModel[] }
        expandableItems: GenericMinimumWageExpandableItemModel[]
    }
    setState: React.Dispatch<React.SetStateAction<{ expandableItems: GenericMinimumWageExpandableItemModel[] }>>
}

const DRIVER_WORK_YEARS_PERIODS_DEFAULT = [
  DriverWorkYearsPeriods.All,
  DriverWorkYearsPeriods.None,
]

const QUALIFICATION_TYPES_DEFAULT = [
    DriverQualificationsType.NotSet,
]

export const GenericMinimumWageExpandableItemSection = ({
    allowedCurrencies,
    driverWorkYears,
    qualificationTypes,
    state,
    setState,
}: Props) => {
    const { t } = useLocalize()

    return (
        <RepeatableSection<GenericMinimumWageExpandableItemModel>
            title={t('configuration.minimumWages.minimalHourRateTitle')}
            newRow={() => ({
                configurationValueId: 0,
                rateValue: 0,
                dateFrom: moment().format(SERVICE_DATE_FORMAT),
                driverYears: 0,
                rateCurrency: state.configuration.currencies[0],
                driverQualifications: Array.isArray(qualificationTypes) && qualificationTypes.length ? qualificationTypes[0] : DriverQualificationsType.Guard,
                isNew: true,
                isEdited: true,
            })}
            columns={[
                {
                    order: 1,
                    label: t('configuration-minimumWage.from-date'),
                    type: "date",
                    get: (r) => moment(r.dateFrom).format(SERVICE_DATE_FORMAT),
                    set: (r, v) => ({ ...r, dateFrom: moment(v).format(SERVICE_DATE_FORMAT) }),
                },
                {
                    order: 3,
                    label: t('common.type'),
                    type: "select",
                    hidden: !Array.isArray(qualificationTypes),
                    options: (qualificationTypes || []).map((value) => ({
                        value, label: getDriverQualificationTypeName(value, t),
                    })),
                    get: (r) => typeof r.driverQualifications === 'number' ? getDriverQualificationType(r.driverQualifications) : r.driverQualifications,
                    set: (r, v: DriverQualificationsType) => ({ ...r, driverQualifications: v }),
                },
                {
                    order: 4,
                    label: t('common.amount'),
                    type: "price",
                    get: (r) => r.rateValue,
                    set: (r, v: number) => ({ ...r, rateValue: Number(v) }),
                },
                {
                    order: 5,
                    label: t("configuration.countries.currency"),
                    type: "select", //"currency",
                    options: state.configuration.currencies.filter(c => Array.isArray(allowedCurrencies) ? allowedCurrencies.includes(c.code) : true).map((c) => ({ label: `${c.code} - ${c.name}`, value: c.code })).sort((a, b) => a.label.localeCompare(b.label)),
                    get: (r) => r.rateCurrency.code,
                    set: (r, v) => {
                        const selected = state.configuration.currencies.find(c => c.code === v)
                        return ({ ...r, rateCurrency: selected })
                    }
                },
                {
                    order: 6,
                    label: t("configuration.minimumWages.driverWorkYears"),
                    type: "select",
                    hidden: !Array.isArray(driverWorkYears),
                    options: (driverWorkYears || []).map(( value ) => ({ value: value.toString(), label: getDriverWorkYearsPeriodsWorkName(value, t), })),
                    get: (r) => r.driverYears,
                    set: (r, v) => {
                        return ({ ...r, driverYears: v as DriverWorkYearsPeriods })
                    }
                },
            ]}
            canAddRow={true}
            rows={state.expandableItems}
            setRows={(next) => {
                setState(prev => {
                    const current = prev.expandableItems;
                    const nextRows = typeof next === "function" ? (next as (p: typeof current) => typeof current)(current) : next;
                    return { ...prev, expandableItems: nextRows };
                });
            }}
            sortRows={(a, b) => {
                const da = moment(a.dateFrom)
                const db = moment(b.dateFrom)
                const ta = da.valueOf()
                const tb = db.valueOf()
                if (ta === tb) return 0
                return ta > tb ? -1 : 1
            }}
        />
    )
}
